import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { LicenseCodeFilter } from "../Components/FilterComponent";
import { LicenseCodeColumns } from "./Columns";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import CommonButton from "../../../global/components/CommonButton";
import { RiExportFill, RiImportFill } from "react-icons/ri";
import downloadCsv from "../../../global/utils/downloadCsv";
import { Modal } from "antd";
import { ImportFile } from "../../../global/components/ImportForm";
import { hasRole } from "../../../global/hooks/getUserRole";

const initialFilterState = {
  CodeNo: "",
  CodeName: "",
  Item: null,
  Code: "",
};

export const LicenseCode = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "Item",
    order: "asc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const navigate = useNavigate();
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const {
    data: optionsData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);
  const modifiedItems = getModifiedResponse(optionsData, "items");
  // const modifiedTypes = getModifiedResponse(optionsData, "types");

  const breadcrumbItems = [
    {
      label: "License Code",
      href: "/licensecode",
    },
  ];

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;
  const { data, loading, error, refetch } = useGet(url);

  let deleteUrl;
  deleteUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteMultiple,
  } = useDelete(deleteUrl);

  const csvQuery = query;
  csvQuery.delete("limit");
  csvQuery.append("export", "csv");
  const updatedCsvQuery = csvQuery.toString();

  const exportCodes = updatedCsvQuery
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?${updatedCsvQuery}`
    : `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?export=csv`;
  const { refetch: refetchExport } = useGet(shouldFetch ? exportCodes : null);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record) => {
    return {
      onClick: (event) => {
        navigate(`/licensecode/info/${record?.CodeNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    // console.log("filterDict", filterDict);
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "Item",
        order: "asc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "Item",
      order: "asc",
    };

    // Update the parameters in the state
    setParams(params);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteMultiple({ ids: selectedRowKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.CodeNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  const handleExportClick = async () => {
    try {
      setExportLoading(true);
      const freshData = await refetchExport();
      if (freshData) {
        const location = window.location;
        downloadCsv(freshData, location, "License Code");
        openConfirmNotification("Success");
      } else {
        throw new Error("No export data found");
      }
    } catch (error) {
      console.error(error);
      openErrorNotification("Something went wrong.");
    }
    setExportLoading(false);
    setShouldFetch(false);
  };
  const buttonsData = hasRole(1, 2, 3)
    ? [
        {
          label: "Export",
          icon: <RiExportFill className="!text-[14px] font-medium" />,
          loading: exportLoading,
          onClick: () => {
            setShouldFetch(true);
            setTimeout(() => {
              handleExportClick();
            }, 200);
          },
        },
        {
          label: "Import",
          icon: <RiImportFill className="!text-[14px] font-medium" />,
          onClick: () => {
            setImportModal(true);
          },
        },
      ]
    : [];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <LicenseCodeFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setShowModal={setShowModal}
          selectedRowKeys={selectedRowKeys}
          setFilter={setFilter}
          modifiedItems={modifiedItems}
          // modifiedTypes={modifiedTypes}
        />
        <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        />
        <div className="mt-4">
          <GlobalTable
            columns={LicenseCodeColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              modifiedItems,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
      <Modal
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <RiImportFill className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black  ml-2">
              Import File
            </span>
          </div>
        }
        open={importModal}
        width={580}
        onCancel={() => setImportModal(false)}
        closable={false}
        footer={null}
        centered
        destroyOnClose
      >
        <ImportFile
          url={`${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`}
          onCancel={() => setImportModal(false)}
          onSuccess={() => {
            refetch();
            setImportModal(false);
          }}
        />
      </Modal>
    </div>
  );
};
